/**
 * Every GraphQL document the demo-request screen sends. They go through
 * `crmGql` (src/lib/crmApi.js) to the admin BFF's `POST /crm`, and are built
 * against the CRM's OWN schema:
 * `marketing/parsina.bff.crm/src/graphql/typeDefs/{customer,pipeline}.js`.
 *
 * Timestamps are epoch-millisecond STRINGS end to end (`createdAtMs`,
 * `dueAtMs`, …). No write carries an actor: the CRM stamps the named human
 * from the token `sub`, and no input type there has an actor field.
 */

const LEAD_FIELDS = `
  id customerId customerName title status source ownerId product
  statusChangedMs closedAtMs lostReason note createdAtMs updatedAtMs
  intent subjectRef allowedNextStatuses
  requestDetails {
    practiceType dentistCount demoMode preferredTime currentSystem
    organization city province message formId landingUrl reference
    utm { source medium campaign content term }
    slug councilNumber doctorDisplayName preferredChannel
  }
`;

const INTERACTION_FIELDS = `
  id channel direction outcome leadId subject body durationSeconds actorId occurredAtMs recordedAtMs
`;

const FOLLOW_UP_FIELDS = `
  id title note dueAtMs status assignedTo leadId createdBy createdAtMs completedAtMs completedBy outcomeNote
`;

const NOTE_FIELDS = 'id body actorId isPinned createdAtMs';

export const DEMO_LEADS_QUERY = `
  query DemoRequests(
    $intent: String, $statuses: [String!], $ownerId: String, $unassignedOnly: Boolean,
    $createdFromMs: String, $createdToMs: String, $query: String, $limit: Int, $offset: Int,
    $withMobile: Boolean = false
  ) {
    crmLeads(
      intent: $intent, statuses: $statuses, ownerId: $ownerId, unassignedOnly: $unassignedOnly,
      createdFromMs: $createdFromMs, createdToMs: $createdToMs, query: $query, limit: $limit, offset: $offset
    ) {
      total
      leads { ${LEAD_FIELDS} customerMobile @include(if: $withMobile) }
    }
  }
`;

/** The nav badge and the tab count: how many requests nobody has answered. */
export const DEMO_NEW_COUNT_QUERY = `
  query DemoRequestsNewCount($intent: String = "demo_request") {
    crmLeads(intent: $intent, statuses: ["new"], limit: 1) { total }
  }
`;

/** Open follow-ups already past due, to flag their leads in the table (T6). */
export const OVERDUE_FOLLOW_UPS_QUERY = `
  query DemoRequestsOverdue($dueBeforeMs: String) {
    crmFollowUps(status: "open", dueBeforeMs: $dueBeforeMs, limit: 200) {
      followUps { id leadId dueAtMs }
    }
  }
`;

export const DEMO_LEAD_QUERY = `
  query DemoRequest($id: ID!) {
    crmLead(id: $id) { ${LEAD_FIELDS} }
  }
`;

/** Everything the drawer shows about the person and the history, in one round trip. */
export const DEMO_LEAD_CONTEXT_QUERY = `
  query DemoRequestContext($customerId: ID!, $leadId: ID!) {
    crmCustomer(id: $customerId) {
      customer {
        id firstName lastName title displayName fullName lifecycle
        doNotContact doNotContactReason archivedAtMs createdAtMs
      }
      contactPoints { id kind value isPrimary isActive label }
      professions { id specialty organization isPrimary }
    }
    crmInteractions(leadId: $leadId, limit: 200) { total interactions { ${INTERACTION_FIELDS} } }
    crmFollowUps(customerId: $customerId, limit: 200) { total followUps { ${FOLLOW_UP_FIELDS} } }
    crmNotes(customerId: $customerId, limit: 200) { total notes { ${NOTE_FIELDS} } }
  }
`;

/**
 * Reverse lookup — audited by the CRM on EVERY call («who looked up which
 * number»). Sent once when a drawer opens, never per keystroke.
 */
export const FIND_BY_CONTACT_QUERY = `
  query DemoRequestSameNumber($value: String!, $kind: String) {
    crmFindByContact(value: $value, kind: $kind) { id fullName createdAtMs archivedAtMs }
  }
`;

export const CUSTOMER_DEMO_LEADS_QUERY = `
  query DemoRequestsOfCustomer($customerId: ID!, $intent: String = "demo_request") {
    crmLeads(customerId: $customerId, intent: $intent, limit: 5) {
      leads { id status createdAtMs requestDetails { reference } }
    }
  }
`;

export const UPDATE_LEAD = `
  mutation DemoRequestUpdate($leadId: ID!, $input: UpdateLeadInput!) {
    crmUpdateLead(leadId: $leadId, input: $input) { ${LEAD_FIELDS} }
  }
`;

export const SET_LEAD_STATUS = `
  mutation DemoRequestSetStatus($leadId: ID!, $status: String!, $lostReason: String) {
    crmSetLeadStatus(leadId: $leadId, status: $status, lostReason: $lostReason) { ${LEAD_FIELDS} }
  }
`;

export const LOG_INTERACTION = `
  mutation DemoRequestLogContact($customerId: ID!, $input: InteractionInput!) {
    crmLogInteraction(customerId: $customerId, input: $input) { ${INTERACTION_FIELDS} }
  }
`;

export const CREATE_FOLLOW_UP = `
  mutation DemoRequestFollowUp($customerId: ID!, $input: FollowUpInput!) {
    crmCreateFollowUp(customerId: $customerId, input: $input) { ${FOLLOW_UP_FIELDS} }
  }
`;

export const SET_FOLLOW_UP_STATUS = `
  mutation DemoRequestFollowUpStatus($followUpId: ID!, $status: String!, $outcomeNote: String) {
    crmSetFollowUpStatus(followUpId: $followUpId, status: $status, outcomeNote: $outcomeNote) { ${FOLLOW_UP_FIELDS} }
  }
`;

export const ADD_NOTE = `
  mutation DemoRequestAddNote($customerId: ID!, $body: String!) {
    crmAddNote(customerId: $customerId, body: $body) { ${NOTE_FIELDS} }
  }
`;

export const ARCHIVE_CUSTOMER = `
  mutation DemoRequestArchive($id: ID!, $reason: String) {
    crmArchiveCustomer(id: $id, reason: $reason) { customer { id archivedAtMs } }
  }
`;
